import { X } from 'lucide-react-native';
import type { ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';

type BannerTone = 'primary' | 'info';

/**
 * The frame stays neutral (matching the composer's `outline-300` border) so the
 * banner reads as part of the composer area. `tone` colors only the accents —
 * the "New" badge and the inline action — so a nudge can carry its feature's
 * color (e.g. `info` for On your honor) without a heavy colored frame.
 */
const TONE: Record<BannerTone, { badge: string; action: string }> = {
  primary: { badge: 'bg-primary-100 text-primary-700', action: 'text-primary-600' },
  info: { badge: 'bg-info-50 text-info-700', action: 'text-info-700' },
};

interface ComposerContextBannerProps {
  /** The nudge copy — a string, or composed content (e.g. text with an inline icon). */
  message: ReactNode;
  /** Optional leading badge label, e.g. "New", colored by `tone`. */
  badge?: string;
  /** Optional inline action label (e.g. "Review", "Turn on"). Omit for a dismiss-only nudge. */
  actionLabel?: string;
  /** Invoked when the action is pressed. Required only if `actionLabel` is set. */
  onAction?: () => void;
  /** Invoked when the banner is dismissed via the close control. */
  onDismiss: () => void;
  /** Accent color for the badge + action. Defaults to `primary` (green house). */
  tone?: BannerTone;
  /**
   * Vertical alignment of the row layout. `center` (default) suits single-line nudges;
   * `start` top-aligns the badge and close when the message wraps to multiple lines.
   * Ignored when `layout="stack"`.
   */
  align?: 'center' | 'start';
  /**
   * `row` (default): badge, message, action, and close in one horizontal row.
   * `stack`: badge and close in a header row, message full-width below. Use for
   * multi-line messages where a row layout would leave a gap between the text and
   * the close button.
   */
  layout?: 'row' | 'stack';
  /** Accessibility label for the action; defaults to `actionLabel`. */
  actionAccessibilityLabel?: string;
  /** Accessibility label for the close control. */
  dismissAccessibilityLabel?: string;
}

/**
 * A dismissible context strip shown directly above the message composer,
 * surfacing a nudge inline where the provider is typing: an optional "New" badge,
 * a short message (which may include an inline icon), an optional inline text
 * action, and a functional close (X). The frame matches the composer.
 *
 * Presentational and content-agnostic: callers supply the copy, badge, action,
 * tone, and handlers, and own visibility (render it only when the nudge
 * applies). Shared so every composer nudge — SMS opt-in, specialist follow-up,
 * feature onboarding — reads as one system instead of parallel banners.
 */
export function ComposerContextBanner({
  message,
  badge,
  actionLabel,
  onAction,
  onDismiss,
  tone = 'primary',
  align = 'center',
  layout = 'row',
  actionAccessibilityLabel,
  dismissAccessibilityLabel = 'Dismiss',
}: ComposerContextBannerProps) {
  const toneClasses = TONE[tone];

  const dismissButton = (
    <Pressable
      onPress={onDismiss}
      hitSlop={ICON_BUTTON_HIT_SLOP}
      className="rounded-md p-1 data-[hover=true]:bg-background-50"
      {...getAccessibilityProps({ role: 'button', label: dismissAccessibilityLabel })}>
      <Icon as={X} size="sm" className="text-typography-500" />
    </Pressable>
  );

  if (layout === 'stack') {
    return (
      <VStack className="mb-2 gap-1.5 rounded-lg border border-outline-300 bg-background-0 px-3 py-2">
        <HStack className={twJoin('items-center', badge ? 'justify-between' : 'justify-end')}>
          {badge ? (
            <Text
              size="xs"
              className={twJoin(
                'shrink-0 rounded-md px-1.5 py-0.5 font-semibold uppercase tracking-wide',
                toneClasses.badge
              )}>
              {badge}
            </Text>
          ) : null}
          <HStack className="items-center gap-2">
            {actionLabel && onAction ? (
              <Pressable
                onPress={onAction}
                hitSlop={ICON_BUTTON_HIT_SLOP}
                className="rounded-md px-2 py-1 data-[hover=true]:bg-background-50"
                {...getAccessibilityProps({
                  role: 'button',
                  label: actionAccessibilityLabel ?? actionLabel,
                })}>
                <Text size="sm" className={twJoin('font-semibold', toneClasses.action)}>
                  {actionLabel}
                </Text>
              </Pressable>
            ) : null}
            {dismissButton}
          </HStack>
        </HStack>
        {typeof message === 'string' ? (
          <Text size="sm" className="text-typography-800">
            {message}
          </Text>
        ) : (
          message
        )}
      </VStack>
    );
  }

  return (
    <HStack
      className={twJoin(
        'mb-2 min-h-[44px] gap-2 rounded-lg border border-outline-300 bg-background-0 px-3 py-2',
        align === 'start' ? 'items-start' : 'items-center'
      )}>
      {badge ? (
        <Text
          size="xs"
          className={twJoin(
            'shrink-0 rounded-md px-1.5 py-0.5 font-semibold uppercase tracking-wide',
            toneClasses.badge,
            align === 'start' && 'self-start'
          )}>
          {badge}
        </Text>
      ) : null}

      {typeof message === 'string' ? (
        <Text size="sm" className="flex-1 text-typography-800">
          {message}
        </Text>
      ) : (
        message
      )}

      {actionLabel && onAction ? (
        <Pressable
          onPress={onAction}
          hitSlop={ICON_BUTTON_HIT_SLOP}
          className="rounded-md px-2 py-1 data-[hover=true]:bg-background-50"
          {...getAccessibilityProps({
            role: 'button',
            label: actionAccessibilityLabel ?? actionLabel,
          })}>
          <Text size="sm" className={twJoin('font-semibold', toneClasses.action)}>
            {actionLabel}
          </Text>
        </Pressable>
      ) : null}

      <Pressable
        onPress={onDismiss}
        hitSlop={ICON_BUTTON_HIT_SLOP}
        className={twJoin(
          'rounded-md p-1 data-[hover=true]:bg-background-50',
          align === 'start' && 'self-start'
        )}
        {...getAccessibilityProps({ role: 'button', label: dismissAccessibilityLabel })}>
        <Icon as={X} size="sm" className="text-typography-500" />
      </Pressable>
    </HStack>
  );
}
